import { useState, useEffect, useMemo } from 'react'
import { ExternalLink, ArrowUp, ArrowDown, Minus, Bug, HelpCircle, X, Loader2, Flame, Check } from 'lucide-react'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { TEMPLATE_BADGE_COLORS, getTemplateBadgeLabel } from '../utils/taskHelpers'
import api from '@/api/client'
import type { WaitingJiraTask } from '@/api/types'

interface WaitingJiraViewProps {
  personName: string
  onClose: () => void
}

export function WaitingJiraView({ personName, onClose }: WaitingJiraViewProps) {
  const [tasks, setTasks] = useState<WaitingJiraTask[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set())
  const [isPreparing, setIsPreparing] = useState(false)
  const [prepareResult, setPrepareResult] = useState<{ updated: string[]; errors: { key: string; error: string }[] } | null>(null)

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true)
      try {
        const response = await api.presalesDashboard.getWaitingTasksWithJira(personName)
        if (response.success && response.data) {
          setTasks(response.data)
        }
      } catch (e) {
        console.error('Failed to fetch waiting Jira tasks:', e)
      } finally {
        setIsLoading(false)
      }
    }
    fetchData()
  }, [personName])

  const toggleSelect = (taskId: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(taskId)) next.delete(taskId)
      else next.add(taskId)
      return next
    })
    setPrepareResult(null)
  }

  const clearSelection = () => {
    setSelectedIds(new Set())
    setPrepareResult(null)
  }

  const selectedTasks = useMemo(() => tasks.filter((t) => selectedIds.has(t.id)), [tasks, selectedIds])
  const unselectedTasks = useMemo(() => tasks.filter((t) => !selectedIds.has(t.id)), [tasks, selectedIds])

  // Extract Jira key from a task
  const getJiraKey = (task: WaitingJiraTask): string => {
    const match = task.jira_ticket?.match(/([A-Z][A-Z0-9]+-\d+)/i)
    return match ? match[1].toUpperCase() : task.jira_ticket || ''
  }

  const handlePrepareHuddle = async () => {
    if (selectedTasks.length === 0) return
    setIsPreparing(true)
    setPrepareResult(null)
    try {
      const tickets = selectedTasks.map((t) => getJiraKey(t)).filter(Boolean)
      const response = await api.jira.prepareHuddle(tickets)
      if (response.success && response.data) {
        setPrepareResult({ updated: response.data.updated, errors: response.data.errors })
        // Refresh the task list to show updated Jira data
        const refreshRes = await api.presalesDashboard.getWaitingTasksWithJira(personName)
        if (refreshRes.success && refreshRes.data) {
          setTasks(refreshRes.data)
        }
      }
    } catch (e) {
      console.error('Failed to prepare huddle:', e)
    } finally {
      setIsPreparing(false)
    }
  }

  return (
    <Card className="p-5">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-semibold text-amber-400 uppercase tracking-wide">
            Waiting — Jira Tracker
          </h3>
          {!isLoading && (
            <span className="text-xs text-[var(--text-muted)]">
              {tasks.length} ticket{tasks.length !== 1 ? 's' : ''}
            </span>
          )}
        </div>
        <button
          onClick={onClose}
          className="p-1 rounded hover:bg-[var(--bg-primary)] text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-5 w-5 animate-spin text-[var(--text-muted)]" />
          <span className="ml-2 text-sm text-[var(--text-muted)]">Loading Jira data…</span>
        </div>
      ) : tasks.length === 0 ? (
        <p className="text-sm text-[var(--text-muted)] py-4">
          No waiting tasks with Jira tickets assigned.
        </p>
      ) : (
        <div className="space-y-4">
          {/* ===== Huddle Shortlist ===== */}
          {selectedTasks.length > 0 && (
            <div className="rounded-lg border border-purple-700/40 bg-purple-900/10 p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Flame className="h-4 w-4 text-purple-400" />
                  <span className="text-sm font-semibold text-purple-300">
                    Huddle Shortlist
                  </span>
                  <span className="text-xs text-purple-400/70">
                    {selectedTasks.length} selected
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={clearSelection}
                    className="text-xs text-[var(--text-muted)] hover:text-[var(--text-secondary)] transition-colors"
                  >
                    Clear all
                  </button>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={handlePrepareHuddle}
                    disabled={isPreparing}
                  >
                    {isPreparing ? (
                      <>
                        <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                        Updating…
                      </>
                    ) : (
                      <>
                        <Flame className="h-3.5 w-3.5 mr-1" />
                        Prepare for Huddle
                      </>
                    )}
                  </Button>
                </div>
              </div>

              {/* Result banner */}
              {prepareResult && (
                <div className={`mb-3 p-2 rounded text-xs ${prepareResult.errors.length > 0 ? 'bg-amber-900/20 border border-amber-700/40' : 'bg-green-900/20 border border-green-700/40'}`}>
                  {prepareResult.updated.length > 0 && (
                    <div className="flex items-center gap-1 text-green-300">
                      <Check className="h-3 w-3" />
                      {prepareResult.updated.length} ticket{prepareResult.updated.length !== 1 ? 's' : ''} updated — Priority → High, Label → huddle
                    </div>
                  )}
                  {prepareResult.errors.length > 0 && (
                    <div className="text-amber-300 mt-1">
                      {prepareResult.errors.length} failed: {prepareResult.errors.map((e) => e.key).join(', ')}
                    </div>
                  )}
                </div>
              )}

              <p className="text-[10px] text-purple-400/60 mb-3">
                "Prepare for Huddle" sets each ticket to High priority and adds the "huddle" label in Jira.
              </p>

              <div className="space-y-2">
                {selectedTasks.map((task) => (
                  <WaitingJiraCard
                    key={task.id}
                    task={task}
                    selected
                    onToggle={() => toggleSelect(task.id)}
                  />
                ))}
              </div>
            </div>
          )}

          {/* ===== All Tickets ===== */}
          {unselectedTasks.length > 0 && (
            <div className="space-y-2">
              {selectedTasks.length > 0 && (
                <div className="text-xs text-[var(--text-muted)] uppercase tracking-wide font-medium pt-1">
                  Other Tickets
                </div>
              )}
              {unselectedTasks.map((task) => (
                <WaitingJiraCard
                  key={task.id}
                  task={task}
                  selected={false}
                  onToggle={() => toggleSelect(task.id)}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </Card>
  )
}

// ==================== Card ====================

interface WaitingJiraCardProps {
  task: WaitingJiraTask
  selected: boolean
  onToggle: () => void
}

function WaitingJiraCard({ task, selected, onToggle }: WaitingJiraCardProps) {
  // Extract Jira key for URL
  const keyMatch = task.jira_ticket?.match(/([A-Z][A-Z0-9]+-\d+)/i)
  const jiraKey = keyMatch ? keyMatch[1].toUpperCase() : task.jira_ticket || ''

  // Jira URL
  const jiraUrl = task.jira_ticket?.startsWith('http')
    ? task.jira_ticket
    : `https://frankieone.atlassian.net/browse/${jiraKey}`

  // Status category color
  let statusColor = '#94a3b8'
  let statusBg = 'rgba(148,163,184,0.15)'
  if (task.jira_status_category === 'new') {
    statusColor = '#3b82f6'
    statusBg = 'rgba(59,130,246,0.15)'
  } else if (task.jira_status_category === 'indeterminate') {
    statusColor = '#f59e0b'
    statusBg = 'rgba(245,158,11,0.15)'
  } else if (task.jira_status_category === 'done') {
    statusColor = '#22c55e'
    statusBg = 'rgba(34,197,94,0.15)'
  }

  // Age color
  let ageColor = '#22c55e'
  if (task.jira_age_days !== null) {
    if (task.jira_age_days > 14) ageColor = '#ef4444'
    else if (task.jira_age_days > 7) ageColor = '#f59e0b'
  }

  // Priority style
  const ps = getPriorityStyle(task.jira_priority)
  const PriorityIcon = ps.Icon

  // Issuetype icon
  const IssueIcon = task.jira_issuetype?.toLowerCase() === 'bug' ? Bug : HelpCircle

  // Huddle check
  const isHuddle = task.jira_labels?.some((l) => l.toLowerCase().includes('huddle')) || false

  return (
    <div
      className={`p-3 rounded-lg border transition-colors cursor-pointer ${
        selected
          ? 'bg-purple-900/15 border-purple-700/40 hover:border-purple-600/60'
          : 'bg-[var(--bg-secondary)] border-[var(--border-color)] hover:border-[var(--border-hover)]'
      }`}
      onClick={onToggle}
    >
      {/* Row 1: Checkbox + Deal name + template badge + description */}
      <div className="flex items-start gap-2.5">
        <input
          type="checkbox"
          checked={selected}
          onChange={onToggle}
          onClick={(e) => e.stopPropagation()}
          className="mt-1 h-3.5 w-3.5 rounded border-[var(--border-color)] text-purple-500 focus:ring-purple-500 cursor-pointer flex-shrink-0"
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            {task.deal_name && (
              <span className="text-[10px] text-[var(--text-muted)] truncate max-w-[120px]">
                {task.deal_name}
              </span>
            )}
            {task.template && task.template !== 'other' && (
              <span className={`inline-block px-1 py-0 text-[9px] font-medium rounded border ${TEMPLATE_BADGE_COLORS[task.template] || TEMPLATE_BADGE_COLORS.other}`}>
                {getTemplateBadgeLabel(task.template)}
              </span>
            )}
            {task.is_critical_path && (
              <span className="text-[9px] px-1 py-0 rounded bg-red-900/30 text-red-300 border border-red-700/40 font-medium">CP</span>
            )}
          </div>
          <p className="text-sm text-[var(--text-primary)] leading-snug">
            {task.description}
          </p>
          {task.status_update && (
            <p className="text-xs text-[var(--text-muted)] mt-1 italic">
              {task.status_update}
            </p>
          )}
        </div>
      </div>

      {/* Row 2: Jira details */}
      <div className="flex items-center gap-1.5 mt-2 ml-6 flex-wrap">
        <a
          href={jiraUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="text-[11px] font-mono hover:underline flex items-center gap-0.5"
          style={{ color: statusColor }}
          onClick={(e) => e.stopPropagation()}
        >
          {jiraKey}
          <ExternalLink className="h-2.5 w-2.5" />
        </a>

        {task.jira_status && (
          <span
            className="text-[10px] px-1.5 py-0.5 rounded font-medium"
            style={{ color: statusColor, background: statusBg }}
          >
            {task.jira_status}
          </span>
        )}

        {task.jira_issuetype && (
          <span className="text-[10px] flex items-center gap-0.5 text-[var(--text-muted)]" title={`Type: ${task.jira_issuetype}`}>
            <IssueIcon className="w-3 h-3" />
            {task.jira_issuetype}
          </span>
        )}

        {task.jira_priority && (
          <span
            className="text-[10px] px-1.5 py-0.5 rounded font-medium flex items-center gap-0.5"
            style={{ color: ps.color, background: ps.bg }}
            title={`Priority: ${task.jira_priority}`}
          >
            {PriorityIcon && <PriorityIcon className="w-3 h-3" />}
            {task.jira_priority}
          </span>
        )}

        {task.jira_age_days !== null && (
          <span
            className="text-[10px] font-semibold px-1.5 py-0.5 rounded"
            style={{ color: ageColor, background: `${ageColor}15` }}
            title="Jira ticket age"
          >
            {task.jira_age_days}d old
          </span>
        )}

        {isHuddle && (
          <span className="text-[10px] px-1.5 py-0.5 rounded font-medium bg-purple-900/30 text-purple-300 border border-purple-700/40">
            Huddle
          </span>
        )}

        {task.jira_labels && task.jira_labels.length > 0 && (
          <div className="flex items-center gap-1 flex-wrap">
            {task.jira_labels.filter((l) => !l.toLowerCase().includes('huddle')).slice(0, 3).map((label) => (
              <span
                key={label}
                className="text-[9px] px-1 py-0.5 rounded bg-[var(--bg-tertiary)] text-[var(--text-muted)] border border-[var(--border-color)]"
                title={label}
              >
                {label.length > 20 ? label.slice(0, 18) + '…' : label}
              </span>
            ))}
          </div>
        )}

        {task.due_date && (
          <span className="text-[10px] text-[var(--text-muted)] ml-auto" title="Due date">
            Due {task.due_date}
          </span>
        )}
      </div>
    </div>
  )
}

// ==================== Helpers ====================

function getPriorityStyle(priority: string | null): { color: string; bg: string; Icon: typeof ArrowUp | null } {
  if (!priority) return { color: '#94a3b8', bg: 'rgba(148,163,184,0.15)', Icon: null }
  const p = priority.toLowerCase()
  if (p === 'highest' || p === 'high') return { color: '#ef4444', bg: 'rgba(239,68,68,0.15)', Icon: ArrowUp }
  if (p === 'medium') return { color: '#f59e0b', bg: 'rgba(245,158,11,0.15)', Icon: Minus }
  if (p === 'low' || p === 'lowest') return { color: '#22c55e', bg: 'rgba(34,197,94,0.15)', Icon: ArrowDown }
  return { color: '#94a3b8', bg: 'rgba(148,163,184,0.15)', Icon: null }
}
